import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useToast } from '../../hooks/use-toast';
import { authService } from '../../services/auth';

export function MagicLinkSent() {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const email = searchParams.get('email') || 'your email';

  const handleResendEmail = async () => {
    const userEmail = searchParams.get('email');

    if (!userEmail) {
      toast({
        title: 'Email required',
        description: 'Please try logging in again to receive a magic link.',
        variant: 'destructive',
      });
      return;
    }

    setIsResending(true);
    setResendSuccess(false);

    try {
      await authService.resendVerification(userEmail);
      setResendSuccess(true);

      toast({
        title: 'Magic link sent',
        description: 'Please check your inbox for the new magic link.',
      });
    } catch (error) {
      toast({
        title: 'Failed to resend',
        description: error instanceof Error ? error.message : 'Could not resend magic link',
        variant: 'destructive',
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto mb-4">
            <Mail className="h-16 w-16 text-primary" />
          </div>

          <CardTitle className="text-2xl font-bold">
            Check your email
          </CardTitle>

          <CardDescription>
            We've sent you a magic link to log in
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="text-center">
            <p className="text-lg font-semibold text-foreground">
              {email}
            </p>
          </div>

          <Alert className="border-blue-200 bg-blue-50">
            <CheckCircle2 className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              Click the link in your email to sign in instantly
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground text-center">
              The magic link will expire in 24 hours.
            </p>

            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground text-center mb-3">
                Didn't receive the email?
              </p>

              {resendSuccess && (
                <Alert className="border-green-200 bg-green-50 mb-3">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Magic link sent successfully!
                  </AlertDescription>
                </Alert>
              )}

              <Button
                onClick={handleResendEmail}
                disabled={isResending}
                className="w-full"
                variant="outline"
              >
                {isResending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Resending...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Resend Magic Link
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            <p>Check your spam folder if you don't see the email.</p>
          </div>
        </CardContent>

        <CardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            Want to use password instead?{' '}
            <Link to="/login" className="text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}

export default MagicLinkSent;
